import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";

import { Button } from "./Button";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in destructive styling + a warning icon. */
  destructive?: boolean;
  /** Disables both buttons and swaps the confirm label for `loadingLabel`. */
  loading?: boolean;
  loadingLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Centered confirmation modal. Rendered through a portal on `document.body`
 * so it always covers the full viewport regardless of where it's mounted in
 * the tree (no ancestor stacking / overflow / transform can clip it). Closes
 * on Escape or backdrop click (suppressed while `loading`), and locks body
 * scroll while open.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  loadingLabel = "Working…",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onCancel();
    };
    document.addEventListener("keydown", onKey);
    // Lock background scroll while the modal is up.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, loading, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex animate-fade-in items-center justify-center bg-overlay/70 p-4 backdrop-blur-[2px]"
      onClick={() => {
        if (!loading) onCancel();
      }}
      role="presentation"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md animate-scale-in rounded-2xl border border-border bg-surface p-6 shadow-2xl ring-1 ring-black/5"
      >
        <div className="flex items-start gap-3.5">
          {destructive && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive-light">
              <AlertTriangle className="h-5 w-5 text-destructive-foreground" />
            </div>
          )}
          <div className="min-w-0 flex-1 pt-0.5">
            <h3 className="text-base font-semibold tracking-display-tight text-copy">
              {title}
            </h3>
            {description && (
              <div className="mt-1.5 text-sm leading-relaxed text-copy-muted">
                {description}
              </div>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2.5">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "primary"}
            size="sm"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? loadingLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
